import React, { Suspense, useEffect } from 'react';
import { useQuery } from "react-query";
import * as apiClient from "../api-client";
// Import the CSS
import '../index.css';
import Loader from '../components/LoaderComponent';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import FAQCouple from '../components/HolidayFAQs/CoupleFaq';
import BlogSliderType from '../components/Blogs/BlogSliderType';
import HolidayTypeHotelsList from '../components/HolidayTypeHotelList';
import Slider1 from "../components/CountrySliders/Maldives/CoupleSlider";
import Slider2 from "../components/CountrySliders/Mauritius/CoupleSlider";
import HotelSliderSkeleton from '../components/Skeletons/HotelSliderSkeleton';
import ReadMoreLines from '../components/ReadMoreLines';

// Lazy load the components
// const LazyLatestDestinationCard = React.lazy(() => import('../components/LastestDestinationCard'));
const LazyWhyChoose = React.lazy(() => import('../components/WhyChoose/Couple'));
const LazyVacationTypes = React.lazy(() => import('../components/Home/VacationTypes'));

const PageCouple = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: hotels } = useQuery("fetchAllInclusiveHotels", apiClient.fetchHotels);

  // const [isExpanded, setIsExpanded] = useState(false);
  // const toggleText = () => setIsExpanded(!isExpanded);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Romantic Couple Holidays & Getaways |  Halo Holidays</title>
          <meta name="description" content="Ignite the romance with Halo Holidays' curated collection of couple holidays. Discover secluded beaches, charming cities, and luxurious retreats designed for unforgettable moments together." />
          {/* <meta name="keywords" content="travel, hotels, holidays, deals, holiday packages" /> */}
        </Helmet>
      </HelmetProvider>

      <section className="max-h-640 md:min-h-0 lg:min-h-0 my-8">
        <hr className="mb-10" />
        <h2 className="text-2xl font-semibold items-center justify-center flex mt-10">More reasons to choose us</h2>
        <Suspense fallback={<Loader />}>
          <LazyWhyChoose />
        </Suspense>
        <hr className="mb-10 mt-10" />
      </section>

      <section className="mx-4 my-4 md:mx-0 md:my-0">

        <div className='mt-2 mb-4'>
          <h1 className="text-2xl font-semibold">Couple Holidays</h1>
          <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
            <p className="text-black md:mb-1 md:mt-2">
              Couples holidays are the perfect escape to reignite the spark and create cherished memories that will last a lifetime. Whether you're dreaming of a secluded beach retreat, a cultural city break, or an adventurous mountain escape, Halo Holidays has the perfect romantic getaway tailored to your unique desires.
              <br /><br />
              From the sun-kissed shores of the Maldives and Greece, where you can stroll hand-in-hand on pristine beaches and watch breathtaking sunsets, to the charming cobbled streets of Europe, where history and romance intertwine, we offer a diverse range of <a href='https://www.haloholidays.co.uk/honeymoon-holidays' className='text-halo-pink'>honeymoon holidays</a> and romantic escapes that cater to every couple's unique tastes. Whether you're celebrating a honeymoon, an anniversary, or simply want to escape the ordinary and reconnect with your partner, we'll help you find the perfect destination and experiences to make your romantic dreams a reality.
              <br /><br />
              Indulge in luxurious all-inclusive resorts designed exclusively for couples, where you can unwind in private villas with your own pool, savor intimate candlelit dinners under the stars, and pamper yourselves with rejuvenating spa treatments. Or, embark on a couples' adventure, exploring exotic destinations like Thailand or Sri Lanka, hiking through scenic landscapes, or learning new skills together in a cooking class or language course. Our carefully curated selection of couple holidays ensures that your romantic getaway is filled with unforgettable moments and cherished memories, whether you're celebrating a special occasion or simply want to reconnect and create a deeper bond with your loved one.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Handpicked Romantic Couple Resorts</h2>
            <p className='text-black'>
              Whether you're envisioning a secluded overwater bungalow in the Maldives, a charming villa nestled in the Tuscan countryside, or a lavish suite with a view of the Eiffel Tower, Halo Holidays has a curated collection of <a href='https://www.haloholidays.co.uk/luxury-holidays' className='text-halo-pink'>luxury holidays</a> and romantic retreats that cater to your unique desires. Our handpicked couples' resorts boast stunning settings, world-class amenities like private pools and gourmet dining, and personalized service that goes above and beyond to create a truly unforgettable experience for you and your loved one.
              <br /><br />
              Indulge in intimate dinners under the stars, rejuvenating couples' spa treatments, and activities designed to rekindle the spark and create lasting memories. Let us transform your romantic getaway into a luxurious escape where love blossoms and dreams come true.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>More Than Just Couples - Explore Our Diverse Holiday Styles</h2>
            <p className='text-black'>
              Looking for a different kind of adventure? Halo Holidays offers a diverse range of holiday options beyond romantic getaways. Discover exciting family holidays filled with fun and adventure for all ages, embark on a group getaway with friends to create lifelong memories, or perhaps even a last-minute holiday to Greece, where ancient wonders and sun-kissed beaches await. If you're seeking a personal journey of self-discovery, we also offer inspiring solo travel experiences, from yoga retreats in Bali to cultural immersion tours in Japan.
              <br /><br />
              For those who love the thrill of spontaneity, our last-minute holidays can whisk you away to paradise at a moment's notice. Whether you're dreaming of a relaxing beach escape or an action-packed city break, we've got deals that will fit your schedule and budget.
            </p>
          </ReadMoreLines>
        </div>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='mt-6 -mb-2 md:mt-0 md:mb-2'>
              <h2 className="text-xl font-semibold">Maldives Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Couple" holidayCountry="Maldives" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider1 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='md:mt-4 -mb-2 md:mb-2'>
              <h2 className="text-xl font-semibold">Mauritius Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Couple" holidayCountry="Mauritius" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider2 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <FAQCouple />

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">Latest All Inclusive Destinations</h2>
      <p>Most recent all-inclusive destinations added by our hosts</p>
      </Suspense> */}
        {/* Displaying the top row hotels */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div>
       */}
        {/* Displaying the bottom row hotels */}
        {/* <div className="grid md:grid-cols-3 gap-4">
        {bottomRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}

        <Suspense fallback={<Loader />}>
          <div className='p-4 md:p-0 md:mt-12 md:mb-12'>
            <LazyVacationTypes />
          </div>
        </Suspense>

        <section className='p-4 md:p-0'>
          <h2 className='text-2xl font-semibold mb-4'>Related Blogs</h2>
          <BlogSliderType blogType='love_and_travel' />
        </section>

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">More All Inclusive Destinations</h2>
      <p>Explore more all-inclusive destinations</p>
      </Suspense> */}

        {/* Optionally, display more hotels with AllInclusiveHotelCard */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyAllInclusiveHotelCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}
      </section>
    </>
  );
};

export default PageCouple;
